import React from 'react'
import { graphql } from "gatsby"
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'

import { Element } from "components/shared/base"
import FullWidthImage from 'components/shared/FullWidthImage'
import Hero from 'components/Hero'
import ImageAndTextSection from 'components/ImageAndTextSection'
import FourColumn from 'components/FourColumn'
import Layout from 'components/Layout'
import SEO from 'components/SEO'
import SingleColumn from 'components/SingleColumn'
import TwoColumn from 'components/TwoColumn'
import TwoColumnTitleDescription from 'components/TwoColumnTitleDescription'

import {
  Query
} from "types/__generated__/gatsby-graphql"

export const homepageQuery = graphql`
  query Homepage($language: String!) {
    locales: allLocale(filter: {ns: {in: ["shared"]}, language: {eq: $language}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    },
    prismicHomepage {
      _previewable
      data {
        body {
          ... on PrismicHomepageDataBodyFourColumn {
            id
            items {
              button_text
              button_url {
                link_type
                url
              }
              text
              title {
                html
                richText
                text
              }
            }
            primary {
              border_above_repeatable_items
              headline {
                html
                richText
                text
              }
            }
            slice_type
          }
          ... on PrismicHomepageDataBodyFullWidthImage {
            id
            primary {
              image {
                alt
                gatsbyImageData
                url
              }
            }
            slice_type
          }
          ... on PrismicHomepageDataBodyHero {
            id
            primary {
              background_color
              headline {
                html
                richText
                text
              }
              image {
                alt
                gatsbyImageData
                url
              }
              intro
              headline_font_color
              headline_width
              hide_intro_top_border
              image_should_be_background
              intro_font_color
            }
            slice_type
          }
          ... on PrismicHomepageDataBodyImageAndText {
            id
            slice_type
            primary {
              background_color
              font_color
              full_bleed_images
              headline {
                html
                richText
                text
              }
            }
            items {
              icon {
                url
                alt
                gatsbyImageData
              }
              image {
                alt
                gatsbyImageData
                url
              }
              mobile_image {
                alt
                gatsbyImageData
                url
              }
              text
              title {
                html
                richText
                text
              }
              title_width
            }
          }
          ... on PrismicHomepageDataBodySingleColumn {
            id
            primary {
              background_color
              eyebrow
              text_color
              wysiwyg {
                html
                richText
                text
              }
            }
            slice_type
          }
          ... on PrismicHomepageDataBodyTitleAndDescriptionTwoColumn {
            id
            primary {
              description
              title {
                html
                richText
                text
              }
              top_border
            }
            slice_type
          }
          ... on PrismicHomepageDataBodyTwoColumn {
            id
            items {
              item_text
              item_title {
                html
                richText
                text
              }
            }
            primary {
              border_above_items
              center_column_content
              headline {
                html
                richText
                text
              },
              title_width
            }
            slice_type
          }
        }
        og_description
        og_image {
          url
        }
        og_title
      }
    }
  }
`
interface OwnProps {
  data: Query
}

type HomepageProps = OwnProps

const componentMap = {
  "hero": Hero,
  "two_column": TwoColumn,
  "full_width_image": FullWidthImage,
  "image_and_text": ImageAndTextSection,
  "four_column": FourColumn,
  "single_column": SingleColumn,
  "title_and_description_two_column": TwoColumnTitleDescription
}

export const Head= ( pageData ) => {
  const { og_description, og_image, og_title } = pageData?.data?.prismicHomepage?.data

  return (
    <SEO
      ogDescription={og_description}
      ogImage={og_image?.url}
      ogTitle={og_title}
      title={og_title}
    />
  )
}

const Homepage: React.FC<HomepageProps>  = ({
  data: { prismicHomepage },
}): React.ReactElement | null => {

  if (!prismicHomepage?.data) return null

  const { body } = prismicHomepage.data
  const componentsData = body;

  return (
    <Layout>
      <Element as="main" color="textPrimary" fontFamily="karla" fontWeight="light">
        {componentsData?.map((component) => {

          const componentName: string = component?.slice_type || "";
          const key = component?.id
          const Component = componentMap[componentName];
          const items = component?.items;

          return (
            <Component key={key} items={items} {...component?.primary} />
          )
        })}
      </Element>
    </Layout>
  )
}

export default withPrismicPreview(Homepage);
